/*
[GNU Affero General Public License (AGPL)](https://www.gnu.org/licenses/agpl-3.0.html)

VeloceUI - A UI component library for Angular v17+ made with care by Colibryx Srl in Italy.
Copyright (C) 2025 Colibryx S.r.l.

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as published by
the Free Software Foundation, either version 3 of the License, or
(at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

This license applies to any accompanying files located directly in the same directory.

For any inquiry, please contact Colibryx S.r.l. at info@colibryx.com.
*/

import {
  ChangeDetectionStrategy,
  Component,
  HostListener,
  type TemplateRef,
  ViewChild,
  computed,
  inject,
  signal,
} from '@angular/core';
import { Title } from '@angular/platform-browser';
import { NavigationEnd, RouterModule, RouterOutlet } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject, takeUntil } from 'rxjs';
import { CbRoutesUtils } from './@classes/routes/cb-routes-utils';
import { AppView } from './@classes/views/common/app.view';
import {
  type CbFeature,
  FeaturesService,
} from './@services/shared/features.service';
import { GlobalTemplateModule } from './directives/global-template/global-template.module';
import { LayoutModule } from './widgets/base/layout/layout.module';
import {
  type CbSidebarItem,
  SidebarComponent,
} from './widgets/base/sidebar/sidebar.component';

import { environment } from 'src/environments/environment';
import { AuthTenantService } from './@services/shared/auth-tenant.service';
import { BarcodeScannerService } from './@services/shared/barcode-scanner.service';
import { NotificationsStateService } from './@services/shared/notifications-state.service';
import { SimilarPricesLauncherService } from './@services/generic/similar-prices-launcher.service';
import { TenantModulesAccessService } from './@services/shared/tenant-modules-access.service';
import { NotificationPopoverComponent } from './widgets/app/notification-popover/notification-popover.component';
import { ChoiceWidgetComponent } from './widgets/base/choice/choice-widget/choice-widget.component';
import { EmptyStateComponent } from './widgets/base/empty-state/empty-state.component';
import { LoaderModule } from './widgets/base/loader/loader.module';
import {
  type CbTopNavbarItem,
  TopNavbarComponent,
} from './widgets/base/top-navbar/top-navbar.component';

@Component({
  selector: 'app-root',
  imports: [
    RouterOutlet,
    LayoutModule,
    SidebarComponent,
    TranslateModule,
    RouterModule,
    GlobalTemplateModule,
    EmptyStateComponent,
    LoaderModule,
    TopNavbarComponent,
    ChoiceWidgetComponent,
    NotificationPopoverComponent,
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  providers: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent extends AppView {
  @ViewChild('notificationPopover', { static: false })
  notificationPopoverTemplate?: TemplateRef<unknown>;

  private readonly tenantsService: AuthTenantService =
    inject(AuthTenantService);
  private readonly featuresService: FeaturesService = inject(FeaturesService);
  private readonly tenantModulesAccess: TenantModulesAccessService = inject(
    TenantModulesAccessService,
  );
  private readonly titleService: Title = inject(Title);
  private readonly translateService: TranslateService =
    inject(TranslateService);
  private readonly barcodeScannerService: BarcodeScannerService = inject(
    BarcodeScannerService,
  );
  private readonly notificationsStateService: NotificationsStateService = inject(
    NotificationsStateService,
  );
  private readonly similarPricesLauncher = inject(SimilarPricesLauncherService);

  public screenWidth = signal(0);
  public screenHeight = signal(0);

  private destroy$: Subject<void> = new Subject<void>();

  public hasStripeSecretKey = false;

  public isBackoffice = false;

  public isDev = environment.dev;

  public currentOffice = computed(() =>
    this.tenantModulesAccess.currentOffice(),
  );
  public readonly hasRestrictedUserOptions = computed(() => {
    return this.authStateService.currentTenantRole() === 'customer';
  });

  public readonly sidebarNavItems = computed(() => {
    switch (this.currentOffice()) {
      case 'warehouse':
        return this.sidebarNavWarehouseItems;
      case 'administration':
        return this.sidebarNavAdministrationItems;
      case 'planning':
        return this.sidebarNavPlanningItems;
      case 'customer-portal':
        return this.sidebarNavCustomerPortalItems;
      default:
        return [];
    }
  });

  public readonly sidebarFooterItems = computed(() => {
    switch (this.currentOffice()) {
      case 'warehouse':
        return this.sidebarFooterWarehouseItems;
      case 'administration':
        return this.sidebarFooterAdministrationItems;
      case 'planning':
        return this.sidebarFooterPlanningItems;
      case 'customer-portal':
        return this.sidebarFooterCustomerPortalItems;
      default:
        return [];
    }
  });

  public readonly background = computed(() => {
    switch (this.currentOffice()) {
      case 'warehouse':
        return 'linear-gradient(90deg, #001455 0%, #5925DC 29.67%, #001E80 51.91%, #001455 100%)';
      case 'planning':
        return 'linear-gradient(90deg, #095C37 0%, #099250 29.67%, #074D31 51.91%, #095C37 100%)';
      case 'administration':
        return 'linear-gradient(90deg, #57130A 0%, #BC1B06 29.67%, #9E2008 51.91%, #57130A 100%)';
      case 'customer-portal':
        return 'linear-gradient(90deg, #001455 0%, #0032D5 29.67%, #001E80 51.91%, #001455 100%)';
    }
    return 'linear-gradient(90deg, #001455 0%, #7839EE 29.67%, #001E80 51.91%, #001455 100%)';
  });

  public readonly homeRoute = computed(() => {
    if (this.currentOffice() === 'customer-portal') {
      return CbRoutesUtils.paths.c.home.path;
    }

    return CbRoutesUtils.paths.choose_office.path;
  });

  public readonly logo = computed(() => {
    if (this.currentOffice() === 'customer-portal') {
      return '/assets/logo/logo-sertra.png';
    }

    return '/assets/logo/logo-white.svg';
  });

  public readonly sidebarNavWarehouseItems: CbTopNavbarItem[] = [
    {
      type: 'route',
      //label: this.features["w_home"].title,
      routerLink: this.features['w_home'].path,
      icon: this.features['w_home'].icon,
    },
    {
      type: 'route',
      label: this.features['products'].title,
      routerLink: this.features['products'].path,
      icon: this.features['products'].icon,
      color: '#FF4405',
    },
    {
      type: 'route',
      label: this.features['warehouseManagement'].title,
      routerLink: this.features['warehouseManagement'].path,
      icon: this.features['warehouseManagement'].icon,
      color: '#0BA5EC',
    },
    {
      type: 'route',
      label: this.features['incomingOrders'].title,
      routerLink: this.features['incomingOrders'].path,
      icon: this.features['incomingOrders'].icon,
      color: '#D444F1',
    },
    {
      type: 'route',
      label: this.features['outgoingOrders'].title,
      routerLink: this.features['outgoingOrders'].path,
      icon: this.features['outgoingOrders'].icon,
      color: '#15B79E',
    },
    {
      type: 'route',
      label: this.features['w_companies'].title,
      routerLink: this.features['w_companies'].path,
      icon: this.features['w_companies'].icon,
      color: '#EAAA08',
    },
    /* {
      type: "route",
      label: this.features["w_emails"].title,
      routerLink: this.features["w_emails"].path,
      icon: this.features["w_emails"].icon,
      show: this.features["w_emails"].canAccess,
      color: "#6938EF",
    }, */
    {
      type: 'route',
      label: this.features['warehouse'].title,
      routerLink: this.features['warehouse'].path,
      icon: this.features['warehouse'].icon,
      show: this.features['warehouse'].canAccess,
      color: '#335BDD',
    },
    {
      type: 'group',
      icon: 'cb-base-outline-more',
      items: [
        {
          type: 'route',
          label: this.features['works'].title,
          routerLink: this.features['works'].path,
          icon: this.features['works'].icon,
          show: this.features['works'].canAccess,
          color: '#0BA5EC',
        },
        {
          type: 'route',
          label: this.features['alerts'].title,
          routerLink: this.features['alerts'].path,
          icon: this.features['alerts'].icon,
          show: this.features['alerts'].canAccess,
          color: '#EAAA08',
        },
        {
          type: 'route',
          label: this.features['palmtop_operations'].title,
          routerLink: this.features['palmtop_operations'].path,
          icon: this.features['palmtop_operations'].icon,
          show: this.features['palmtop_operations'].canAccess,
          color: '#10B981',
        },
        {
          type: 'route',
          label: this.features['w_vehicles'].title,
          routerLink: this.features['w_vehicles'].path,
          icon: this.features['w_vehicles'].icon,
          show: this.features['w_vehicles'].canAccess,
          color: '#3B82F6',
        },
        {
          type: 'route',
          label: this.features['vehicle_usages'].title,
          routerLink: CbRoutesUtils.paths.w.vehicle_usages.path,
          icon: this.features['vehicle_usages'].icon,
          show: this.features['vehicle_usages'].canAccess,
          color: '#3B82F6',
        },
        {
          type: 'route',
          label: this.features['checklists_backoffice'].title,
          routerLink: this.features['checklists_backoffice'].path,
          icon: this.features['checklists_backoffice'].icon,
          show: this.features['checklists_backoffice'].canAccess,
          color: '#10B981',
        },
        {
          type: 'route',
          label: this.features['inventory'].title,
          routerLink: this.features['inventory'].path,
          icon: this.features['inventory'].icon,
          show: this.features['inventory'].canAccess,
          color: '#6938EF',
        },
        {
          type: 'route',
          label: this.features['palletOccupation'].title,
          routerLink: this.features['palletOccupation'].path,
          icon: this.features['palletOccupation'].icon,
          show: this.features['palletOccupation'].canAccess,
          color: '#0BA5EC',
        },
        {
          type: 'route',
          label: this.features['warehouseStatistics'].title,
          routerLink: this.features['warehouseStatistics'].path,
          icon: this.features['warehouseStatistics'].icon,
          show: this.features['warehouseStatistics'].canAccess,
          color: '#10B981',
        },
      ],
    },
  ];

  public readonly sidebarNavAdministrationItems: CbTopNavbarItem[] = [
    {
      type: 'route',
      //label: this.features["home"].title,
      routerLink: this.features['a_home'].path,
      icon: this.features['a_home'].icon,
    },
    {
      type: 'route',
      label: this.features['pre_billing'].title,
      routerLink: this.features['pre_billing'].path,
      icon: this.features['pre_billing'].icon,
      color: '#0BA5EC',
    },
    {
      type: 'route',
      label: this.features['invoices'].title,
      routerLink: this.features['invoices'].path,
      icon: this.features['invoices'].icon,
      color: '#EAAA08',
    },
    {
      type: 'route',
      label: this.features['financial_movements'].title,
      routerLink: this.features['financial_movements'].path,
      icon: this.features['financial_movements'].icon,
      color: '#0BA5EC',
    },
    /* {
      type: 'route',
      label: this.features['reports'].title,
      routerLink: this.features['reports'].path,
      icon: this.features['reports'].icon,
      color: '#6938EF',
    }, */
    {
      type: 'route',
      label: this.features['calendar'].title,
      routerLink: this.features['calendar'].path,
      icon: this.features['calendar'].icon,
      color: '#0BA5EC',
    },
    {
      type: 'route',
      label: this.features['a_vehicles'].title,
      routerLink: this.features['a_vehicles'].path,
      icon: this.features['a_vehicles'].icon,
      color: '#EAAA08',
    },
    {
      type: 'route',
      label: this.features['a_companies'].title,
      routerLink: this.features['a_companies'].path,
      icon: this.features['a_companies'].icon,
      color: '#EAAA08',
    },
    {
      type: 'route',
      label: this.features['a_staff_members'].title,
      routerLink: this.features['a_staff_members'].path,
      icon: this.features['a_staff_members'].icon,
      color: '#0BA5EC',
    },
    /* {
      type: "route",
      label: this.features["a_emails"].title,
      routerLink: this.features["a_emails"].path,
      icon: this.features["a_emails"].icon,
      color: "#0BA5EC",
    }, */
    {
      type: 'group',
      icon: 'cb-base-outline-more',
      items: [
        {
          type: 'route',
          label: this.features['vehicle_usages'].title,
          routerLink: CbRoutesUtils.paths.a.vehicle_usages.path,
          icon: this.features['vehicle_usages'].icon,
          show: this.features['vehicle_usages'].canAccess,
          color: '#3B82F6',
        },
      ],
    },
  ];

  public readonly sidebarNavPlanningItems: CbTopNavbarItem[] = [
    {
      type: 'route',
      //label: this.features["home"].title,
      routerLink: this.features['p_home'].path,
      icon: this.features['p_home'].icon,
    },
    {
      type: 'route',
      label: this.features['pickups'].title,
      routerLink: this.features['pickups'].path,
      icon: this.features['pickups'].icon,
      color: '#0BA5EC',
    },
    {
      type: 'group',
      label: 'SECTIONS.ROUTES.TRAVELS_GROUP',
      icon: this.features['travels'].icon,
      color: '#0BA5EC',
      items: [
        {
          type: 'route',
          label: this.features['travels'].title,
          routerLink: this.features['travels'].path,
          icon: this.features['travels'].icon,
          color: '#0BA5EC',
        },
        {
          type: 'route',
          label: this.features['subcontractor_travels'].title,
          routerLink: this.features['subcontractor_travels'].path,
          icon: this.features['subcontractor_travels'].icon,
          color: '#0BA5EC',
        },
      ],
    },
    {
      type: 'group',
      label: 'SECTIONS.ROUTES.PLANNING',
      icon: this.features['planning'].icon,
      color: '#0BA5EC',
      items: [
        {
          type: 'route',
          label: this.features['planning'].title,
          routerLink: this.features['planning'].path,
          icon: this.features['planning'].icon,
          color: '#0BA5EC',
        },
        {
          type: 'route',
          label: this.features['vector_planning'].title,
          routerLink: this.features['vector_planning'].path,
          icon: this.features['vector_planning'].icon,
          color: '#0BA5EC',
        },
      ],
    },

    {
      type: 'route',
      label: this.features['loading_list'].title,
      routerLink: this.features['loading_list'].path,
      icon: this.features['loading_list'].icon,
      color: '#0BA5EC',
    },
    {
      type: 'route',
      label: this.features['unloading_list'].title,
      routerLink: this.features['unloading_list'].path,
      icon: this.features['unloading_list'].icon,
      color: '#0BA5EC',
    },
    {
      type: 'group',
      label: 'SECTIONS.ROUTES.ANAGRAFICHE',
      icon: this.features['p_companies'].icon,
      color: '#0BA5EC',
      items: [
        {
          type: 'route',
          label: this.features['p_companies'].title,
          routerLink: this.features['p_companies'].path,
          icon: this.features['p_companies'].icon,
          color: '#0BA5EC',
        },
        {
          type: 'route',
          label: this.features['p_destinations'].title,
          routerLink: this.features['p_destinations'].path,
          icon: this.features['p_destinations'].icon,
          color: '#0BA5EC',
        },
        {
          type: 'route',
          label: this.features['p_vehicles'].title,
          routerLink: this.features['p_vehicles'].path,
          icon: this.features['p_vehicles'].icon,
          color: '#0BA5EC',
        },
        {
          type: 'route',
          label: this.features['p_staff_members'].title,
          routerLink: this.features['p_staff_members'].path,
          icon: this.features['p_staff_members'].icon,
          color: '#0BA5EC',
        },
      ],
    },
    {
      type: 'route',
      label: this.features['calendar'].title,
      routerLink: CbRoutesUtils.paths.p.calendar.path,
      icon: this.features['calendar'].icon,
      color: '#0BA5EC',
    },

    {
      type: 'group',
      icon: 'cb-base-outline-more',
      items: [
        {
          type: 'route',
          label: this.features['pallet_vouchers'].title,
          routerLink: this.features['pallet_vouchers'].path,
          icon: this.features['pallet_vouchers'].icon,
          show: this.features['pallet_vouchers'].canAccess,
          color: '#F97316',
        },
        {
          type: 'callback',
          label: 'SIMILAR_PRICES.TITLE',
          icon: 'cb-app-outline-money',
          show: this.features['pickups'].canAccess,
          color: '#0BA5EC',
          callback: () => {
            this.similarPricesLauncher.openStandalone();
          },
        },
        {
          type: 'callback',
          label: 'COMPANIES.DISTRIBUTION_LIST.MENU_LABEL',
          icon: 'cb-stroke-standard-mail-01',
          show: this.features['p_companies'].canAccess,
          color: '#0BA5EC',
          callback: () => {
            void import(
              './modules/companies/dialogs/company-distribution-list-dialog/company-distribution-list-dialog.component'
            ).then((m) => {
              this.dialogService.open(m.CompanyDistributionListDialogComponent, {
                width: '640px',
              });
            });
          },
        },
        {
          type: 'route',
          label: this.features['map'].title,
          routerLink: CbRoutesUtils.paths.p.map.path,
          icon: this.features['map'].icon,
          show: this.features['map'].canAccess,
          color: '#3B82F6',
        },
        {
          type: 'route',
          label: this.features['board_map'].title,
          routerLink: CbRoutesUtils.paths.p.board_map.path,
          icon: this.features['board_map'].icon,
          show: this.features['board_map'].canAccess,
          color: '#3B82F6',
        },
        {
          type: 'route',
          label: this.features['cash_on_delivery'].title,
          routerLink: CbRoutesUtils.paths.p.cash_on_delivery.path,
          icon: this.features['cash_on_delivery'].icon,
          show: this.features['cash_on_delivery'].canAccess,
          color: '#3B82F6',
        },
        {
          type: 'route',
          label: this.features['planning_deadlines'].title,
          routerLink: this.features['planning_deadlines'].path,
          icon: this.features['planning_deadlines'].icon,
          show: this.features['planning_deadlines'].canAccess,
          color: '#EF4444',
        },
        {
          type: 'route',
          label: this.features['vehicle_usages'].title,
          routerLink: CbRoutesUtils.paths.p.vehicle_usages.path,
          icon: this.features['vehicle_usages'].icon,
          show: this.features['vehicle_usages'].canAccess,
          color: '#3B82F6',
        },
        {
          type: 'route',
          label: this.features['works'].title,
          routerLink: CbRoutesUtils.paths.p.works.path,
          icon: this.features['works'].icon,
          show: this.features['works'].canAccess,
          color: '#0BA5EC',
        },
        {
          type: 'route',
          label: this.features['foreign_trips_report'].title,
          routerLink: this.features['foreign_trips_report'].path,
          icon: this.features['foreign_trips_report'].icon,
          show: this.features['foreign_trips_report'].canAccess,
          color: '#0BA5EC',
        },
      ],
    },
  ];

  public readonly sidebarNavCustomerPortalItems: CbTopNavbarItem[] = [
    {
      type: 'route',
      label: this.features['c_home'].title,
      routerLink: this.features['c_home'].path,
      icon: this.features['c_home'].icon,
    },
    {
      type: 'route',
      label: this.features['c_warehouse'].title,
      routerLink: this.features['c_warehouse'].path,
      icon: this.features['c_warehouse'].icon,
    },
    {
      type: 'route',
      label: this.features['c_incoming_orders'].title,
      routerLink: this.features['c_incoming_orders'].path,
      icon: this.features['c_incoming_orders'].icon,
    },
    {
      type: 'route',
      label: this.features['c_outgoing_orders'].title,
      routerLink: this.features['c_outgoing_orders'].path,
      icon: this.features['c_outgoing_orders'].icon,
    },
  ];

  // public readonly sidebarFooterItems: CbTopNavbarItem[] = [
  // 	{
  // 		type: "route",
  // 		//label: "SECTIONS.ROUTES.BACKOFFICE.TITLE",
  // 		routerLink: CbRoutesUtils.paths.backoffice.path,
  // 		icon: "cb-base-bold-admin",
  // 		showSignal: this.authStateService.isSuperadmin,
  // 	},
  // 	{
  // 		type: "route",
  // 		//label: this.features["settings"].title,
  // 		routerLink: this.features["settings"].path,
  // 		icon: this.features["settings"].icon,
  // 	},
  // ];

  public get sidebarFooterWarehouseItems(): CbTopNavbarItem[] {
    return [
      {
        type: 'route',
        routerLink: this.features['w_ai_chatbot'].path,
        icon: this.features['w_ai_chatbot'].icon,
        show: this.features['w_ai_chatbot'].canAccess,
        color: '#3B82F6',
      },
      {
        type: 'callback',
        callback: () => { },
        icon: 'cb-solid-rounded-notification-square',
        popoverTemplate: this.notificationPopoverTemplate, // Ora verrà letto correttamente quando la vista è pronta
        counterSignal: this.notificationsStateService.unreadCount,
      },
      {
        type: 'route',
        routerLink: CbRoutesUtils.paths.backoffice.path,
        icon: 'cb-base-bold-admin',
        showSignal: this.authStateService.isSuperadmin,
      },
      {
        type: 'callback',
        callback: () => {
          window.open(this.router.url, '_blank');
        },
        icon: 'cb-app-bold-share',
      },
      {
        type: 'route',
        routerLink: this.features['w_settings'].path,
        icon: this.features['w_settings'].icon,
      },
    ];
  }

  public get sidebarFooterAdministrationItems(): CbTopNavbarItem[] {
    return [
      {
        type: 'route',
        routerLink: this.features['a_ai_chatbot'].path,
        icon: this.features['a_ai_chatbot'].icon,
        show: this.features['a_ai_chatbot'].canAccess,
        color: '#3B82F6',
      },
      {
        type: 'route',
        //label: "SECTIONS.ROUTES.BACKOFFICE.TITLE",
        routerLink: CbRoutesUtils.paths.backoffice.path,
        icon: 'cb-base-bold-admin',
        showSignal: this.authStateService.isSuperadmin,
      },
      // {
      //   type: 'callback',
      //   callback: () => {},
      //   icon: 'cb-solid-rounded-notification-square',
      //   popoverTemplate: this.notificationPopoverTemplate,
      // },
      {
        type: 'callback',
        callback: () => {
          window.open(this.router.url, '_blank');
        },
        icon: 'cb-app-bold-share',
      },
      {
        type: 'route',
        //label: this.features["settings"].title,
        routerLink: this.features['a_settings'].path,
        icon: this.features['a_settings'].icon,
      },
    ];
  }

  public readonly sidebarFooterPlanningItems: CbTopNavbarItem[] = [
    {
      type: 'route',
      routerLink: this.features['p_ai_chatbot'].path,
      icon: this.features['p_ai_chatbot'].icon,
      show: this.features['p_ai_chatbot'].canAccess,
      color: '#3B82F6',
    },
    {
      type: 'route',
      //label: "SECTIONS.ROUTES.BACKOFFICE.TITLE",
      routerLink: CbRoutesUtils.paths.backoffice.path,
      icon: 'cb-base-bold-admin',
      showSignal: this.authStateService.isSuperadmin,
    },
    {
      type: 'callback',
      callback: () => {
        window.open(this.router.url, '_blank');
      },
      icon: 'cb-app-bold-share',
    },
    {
      type: 'route',
      //label: this.features["settings"].title,
      routerLink: this.features['p_settings'].path,
      icon: this.features['p_settings'].icon,
    },
  ];

  public readonly sidebarFooterCustomerPortalItems: CbTopNavbarItem[] = [
    {
      type: 'route',
      label: this.features['c_settings'].title,
      routerLink: this.features['c_settings'].path,
      icon: this.features['c_settings'].icon,
    },
  ];

  public readonly backofficeNavItems: CbSidebarItem[] = [
    {
      type: 'route',
      label: 'SECTIONS.ROUTES.BACKOFFICE.TENANTS',
      routerLink: CbRoutesUtils.paths.backoffice.tenants.path,
      icon: 'cb-base-outline-tenant',
      //show: () => this.authService.isSuperadmin,
    },
    {
      type: 'route',
      label: 'SECTIONS.ROUTES.BACKOFFICE.USERS',
      routerLink: CbRoutesUtils.paths.backoffice.users.path,
      icon: 'cb-base-outline-users',
      //show: () => this.authService.isSuperadmin
    },
  ];

  public readonly backofficeFooterItems: CbSidebarItem[] = [
    {
      type: 'route',
      label: 'SECTIONS.ROUTES.BACKOFFICE.SELECT_TENANT',
      routerLink: CbRoutesUtils.paths.tenant.path,
      icon: 'cb-base-outline-refresh',
      //show: () => this.authService.isSuperadmin,
    },
  ];

  /* @HostListener("window:resize", ["$event"])
  onWindowResize() {
    // this.screenWidth.set(window.innerWidth);
    // this.screenHeight.set(window.innerHeight);
    // if (window.innerWidth < 768) {
    // 	this.router.navigate([CbRoutesUtils.paths.wp.palmtop.home.path]);
    // }
  }

  @HostListener("document:mousemove", ["$event"])
  onMouseMove(event: MouseEvent) {
    this.realtimeCursorsService.updateCursor$.next({
      x: (event.clientX / this.screenWidth()) * 100,
      y: (event.clientY / this.screenHeight()) * 100,
    });
  } */

  /**
   * Sulla pistola (dispositivo touch) nelle viste palmtop il testo non è
   * selezionabile e il menu contestuale è disabilitato, così i magazzinieri
   * non possono copiare gli UDC a video invece di scansionarli.
   * Da PC (puntatore preciso) non viene applicato nessun blocco.
   */
  private readonly isTouchDevice =
    window.matchMedia?.('(pointer: coarse)').matches ?? false;
  private isPalmtopCopyLocked = false;

  private updatePalmtopCopyLock(url: string): void {
    const palmtopPath = CbRoutesUtils.paths.wp.palmtop.path;
    const cleanUrl = url.split('?')[0].split('#')[0];
    this.isPalmtopCopyLocked =
      this.isTouchDevice &&
      (cleanUrl === palmtopPath || cleanUrl.startsWith(`${palmtopPath}/`));
    document.body.classList.toggle(
      'cb-palmtop-no-copy',
      this.isPalmtopCopyLocked,
    );
  }

  @HostListener('document:contextmenu', ['$event'])
  onContextMenu(event: MouseEvent) {
    if (!this.isPalmtopCopyLocked) return;
    const target = event.target as HTMLElement | null;
    // I campi di input restano utilizzabili normalmente
    if (target?.closest('input, textarea, [contenteditable="true"]')) return;
    event.preventDefault();
  }

  @HostListener('document:visibilitychange', [])
  onVisibilityChange() {
    if (document.visibilityState === 'visible') {
      // La pagina è diventata visibile
      this.handlePageVisible();
    } else {
      // La pagina è nascosta
      this.handlePageHidden();
    }
  }

  handlePageVisible() {
    // Logica da eseguire quando la pagina diventa visibile
    // Ad esempio, aggiornare i dati o verificare lo stato della sessione
    this.authService.onPageVisibilityChange();
  }

  handlePageHidden() {
    // Logica da eseguire quando la pagina diventa nascosta
    // Ad esempio, sospendere aggiornamenti o salvare lo stato
  }

  public readonly isLoadingPrivileges = computed(() => {
    return this.featuresService.isLoading();
  });

  public readonly userId = computed(() => {
    return this.authStateService.currentUserId();
  });

  public readonly tenantName = computed(() => {
    return this.authStateService.currentTenantInfo()?.name;
  });

  /**
   * Updates the page title based on the current route by finding the matching feature
   */
  private updatePageTitle(url: string): void {
    // Find the feature that matches the current URL
    const matchingFeature = this.findFeatureByPath(url);

    if (matchingFeature) {
      // Get the translated title
      this.translateService
        .get(matchingFeature.title)
        .subscribe((translatedTitle: string) => {
          this.titleService.setTitle(`${translatedTitle} - CoreHub`);
        });
    }
  }

  /**
   * Finds a feature by matching its path with the current URL
   */
  private findFeatureByPath(url: string): CbFeature | null {
    const features = this.features;

    // Remove query parameters and fragments from URL
    const cleanUrl = url.split('?')[0].split('#')[0];

    // Find feature with matching path
    for (const featureKey in features) {
      const feature = features[featureKey];

      // Check if the current URL starts with the feature path
      // This handles both exact matches and sub-routes
      if (
        cleanUrl === feature.path ||
        cleanUrl.startsWith(`${feature.path}/`)
      ) {
        return feature;
      }
    }

    return null;
  }

  constructor() {
    super();

    this.router.events.pipe(takeUntil(this.destroy$)).subscribe((event) => {
      if (event instanceof NavigationEnd) {
        const children = this.activatedRoute.children;
        let newOffice: 'warehouse' | 'administration' | 'planning' | 'customer-portal' | undefined;

        if (children?.length > 0) {
          const data = children[0].snapshot.data;
          this.isBackoffice = data?.['backoffice'] ?? false;
          newOffice = data?.['office'] as typeof newOffice;
          this.tenantModulesAccess.currentOffice.set(newOffice);
        } else {
          this.isBackoffice = false;
          if (this.activatedRoute.snapshot.data['office']) {
            newOffice = this.activatedRoute.snapshot.data['office'] as typeof newOffice;
            this.tenantModulesAccess.currentOffice.set(newOffice);
          } else {
            this.tenantModulesAccess.currentOffice.set(undefined);
          }
        }

        // Carica il conteggio delle notifiche non lette per l'office corrente
        if (newOffice) {
          this.notificationsStateService.loadUnreadCount(newOffice);
        }

        // Update page title based on current route
        this.updatePageTitle(event.urlAfterRedirects);

        this.updatePalmtopCopyLock(event.urlAfterRedirects);
      }
    });
  }

  ngOnInit() {
    if (this.isDev) {
      this.confirmationService
        .show({
          title: 'COMMON.DEV_MODE',
          message: 'COMMON.DEV_MODE_MESSAGE',
          dangerous: true,
          confirmText: 'COMMON.CONFIRM',
        })
        .subscribe((result) => {
          if (!result) {
            window.location.href = 'https://corehub.colibryx.com';
          }
        });
    }
    this.screenWidth.set(window.innerWidth);
    this.screenHeight.set(window.innerHeight);

    // if (window.innerWidth < 768) {
    // 	this.router.navigate([CbRoutesUtils.paths.wp.palmtop.home.path]);
    // }

    // Override structuredClone for older browsers //
    try {
      structuredClone({});
    } catch (e) {
      window.structuredClone = (obj: unknown) => {
        return JSON.parse(JSON.stringify(obj));
      };
    }
    // --- //

    // Inizializza la sincronizzazione automatica delle scansioni
    this.barcodeScannerService.initializeSync();

    // Set initial page title based on current route
    this.updatePageTitle(this.router.url);
  }

  public get hasResponsive(): boolean {
    return true;
    //return this.featuresService.canSelect(["responsive"]);
  }

  private get features(): Record<string, CbFeature> {
    return this.featuresService.features;
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
